/* eslint-disable max-lines */
import {RefSelect, debounceTime, Options, TypedFormFieldComponents} from '@fl/cmsch-fe-library';
import {debounce, includes, isEmpty, replace} from 'lodash/fp';
import * as O from 'optics-ts';
import {ComponentType, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Opt, pipe, testRe} from 'ts-opt';

import {CorrectionType} from 'api/gen/CorrectionType';
import {Date} from 'api/gen/Date';
import {EarTag} from 'api/gen/EarTag';
import {positiveOrZeroDecimal80RegexGen} from 'api/gen/PositiveOrZeroDecimal80';
import {StableCode} from 'api/gen/StableCode';
import {technicianNumberRegexGen} from 'api/gen/TechnicianNumber';
import {
    analysisProtocolCorrectionAction,
    analysisProtocolLoadingSelector,
    simpleAnalysisProtocolCorrectionSelector,
    stableDateOptionsLoadingSelector,
} from 'app/analysis-protocol-correction/model';
import {TFunction, useOurTranslation} from 'app/translations';
import {formHelpers, useForm} from 'utils/forms';

import {useDateAndStableChange} from './use-date-and-stable-change';
import {
    apcReportFormName,
    ApcReportFormValues,
    initialApcReportFormValues,
} from '../components/apc-report-form/form-values';
import {validate} from '../components/apc-report-form/validate';
import {correctionTypeOptions} from '../components/overview-table/columns';
import {
    getDateOptions,
    genProtocolStableOptionsBasedOnDate,
    getStableOptions,
    genNonProtocolDateOptionsBasedOnStable,
    genProtocolDateOptionsBasedOnStable,
} from '../utils/get-apc-options';
import {protocolExists} from '../utils/protocol-exists';

type NumberInputType = 'decimal52' | 'decimal80' | 'positiveOrZeroDecimal80';
const formValuesO = O.optic<ApcReportFormValues>();

const roundNumberToPrecision = (value: number, type: NumberInputType): number => {
    // eslint-disable-next-line no-magic-numbers
    const decimal = type === 'decimal52' ? Math.pow(10, 2) : 1;

    return Math.round(decimal * value) / decimal;
};

/** Dependencies required to build the APC form setup contract. */
interface Props {
    /** Ear-tag input ref used to continue field-to-field navigation. */
    earTagRef: RefObject<HTMLInputElement>;
    /** Technician number input ref used for initial focus and resets. */
    technicianNumberRef: RefObject<HTMLInputElement>;
    /** Submit callback passed to the shared form helper. */
    onSubmit(formValues: ApcReportFormValues): void;
}

/** Public setup contract returned by {@link useNewApcReportFormSetup}. */
interface UseNewApcReportFormSetup {
    /** Form wrapper component returned by the shared form helper. */
    Form: ComponentType<{children?: ReactNode}>;
    /** Field components bound to the APC form value shape. */
    Fields: TypedFormFieldComponents<ApcReportFormValues>;
    /** Available correction-type select options. */
    correctionTypeOptions: Options<CorrectionType>;
    /** Currently selected correction type. */
    correctionType: CorrectionType | undefined;
    /** Whether the form is editing an existing correction report. */
    editingCorrectionReport: boolean;
    /** Available proficiency-test dates for the current form context. */
    proficiencyTestDateOptions: Opt<Options<Date>>;
    /** Available stable options for the current form context. */
    stableOptions: Opt<Options<StableCode>>;
    /** Whether date/stable options are still loading. */
    stableDateOptionsLoading: boolean;
    /** Technician number input ref used for focus orchestration. */
    technicianNumberRef: RefObject<HTMLInputElement>;
    /** Updated ear-tag input ref used for focus orchestration. */
    earTagChangeRef: RefObject<HTMLInputElement>;
    /** Proficiency-test date select ref used for focus orchestration. */
    proficiencyTestDateRef: RefSelect;
    /** Stable-number select ref used for focus orchestration. */
    stableNumberRef: RefSelect;
    /** Milk-kg input ref used for focus orchestration. */
    milkKgRef: RefObject<HTMLInputElement>;
    /** APC module translation helper. */
    t: TFunction<'analysisProtocolCorrection'>;
    /** Shared common translation helper. */
    tCommon: TFunction<'common'>;
    /** Whether the stable field should be disabled. */
    stableNumberDisabled: boolean;
    /** Whether the submit action should be disabled. */
    submitDisabled: boolean;
    /** Whether the current correction type is `UPDATE`. */
    isUpdateCorrection: boolean;
    /** Whether submit/validation processing is active. */
    processingForm: boolean;
    /** Fat-percentage input ref used for focus orchestration. */
    fatPercentageRef: RefObject<HTMLInputElement>;
    /** Protein-percentage input ref used for focus orchestration. */
    proteinPercentageRef: RefObject<HTMLInputElement>;
    /** Lactose-percentage input ref used for focus orchestration. */
    lactosePercentageRef: RefObject<HTMLInputElement>;
    /** Somatic-cells input ref used for focus orchestration. */
    somaticCellsRef: RefObject<HTMLInputElement>;
    /** Urea input ref used for focus orchestration. */
    ureaRef: RefObject<HTMLInputElement>;
    /** Submit button ref used for focus orchestration. */
    submitRef: RefObject<HTMLButtonElement>;
    /** Resolves whether the proficiency-test date field should be disabled. */
    proficiencyTestDateDisabled(): boolean;
    /** Handles technician-number changes. */
    onTechnicianNumberChange(value: Opt<string>): void;
    /** Handles ear-tag changes. */
    onEarTagChange(): void;
    /** Handles updated ear-tag changes. */
    onUpdatedEarTagChange(val: Opt<string>): void;
    /** Handles proficiency-test date changes. */
    onProficiencyTestDateChange(val: Opt<Date>): void;
    /** Handles stable-number changes. */
    onStableNumberChange(val: Opt<StableCode>): void;
    /** Handles correction-type changes. */
    onCorrectionTypeChange(value: Opt<CorrectionType>): void;
    /** Handles milk-kg input normalization and focus flow. */
    onMilkKgInput(value: string): void;
    /** Handles fat-percentage input normalization and focus flow. */
    onFatPercentageInput(value: string): void;
    /** Handles protein-percentage input normalization and focus flow. */
    onProteinPercentageInput(value: string): void;
    /** Handles lactose-percentage input normalization and focus flow. */
    onLactosePercentageInput(value: string): void;
    /** Handles somatic-cells input normalization and focus flow. */
    onSomaticCellsInput(value: string): void;
    /** Handles urea input normalization and focus flow. */
    onUreaInput(value: string): void;
    /** Resolves whether a correction-specific field should be disabled. */
    isAdditionalFieldDisabled(field: keyof ApcReportFormValues): boolean;
}

const decimal52Regex = /^\d{1,3}[,.]\d{2}/;
const decimal80Regex = /^\d{1,8}/;

/**
 * Prepares derived state, focus handlers, options, and field callbacks for the APC form.
 *
 * @param props Form refs and submit callback.
 * @returns The configured form helpers, refs, options, and event handlers for {@link ApcReportForm}.
 *
 * @example
 * const setup = useNewApcReportFormSetup({
 *   earTagRef,
 *   technicianNumberRef,
 *   onSubmit: values => dispatch(save(values)),
 * });
 */
// eslint-disable-next-line max-lines-per-function
export const useNewApcReportFormSetup = ({
    earTagRef,
    technicianNumberRef,
    onSubmit,
}: Props): UseNewApcReportFormSetup => {
    const earTagChangeRef = useRef<HTMLInputElement>(null);
    const milkKgRef = useRef<HTMLInputElement>(null);
    const proficiencyTestDateRef: RefSelect = useRef(null);
    const stableNumberRef: RefSelect = useRef(null);
    const fatPercentageRef = useRef<HTMLInputElement>(null);
    const proteinPercentageRef = useRef<HTMLInputElement>(null);
    const lactosePercentageRef = useRef<HTMLInputElement>(null);
    const somaticCellsRef = useRef<HTMLInputElement>(null);
    const ureaRef = useRef<HTMLInputElement>(null);
    const submitRef = useRef<HTMLButtonElement>(null);

    const dispatch = useDispatch();

    const {t, tCommon} = useOurTranslation('analysisProtocolCorrection');
    const setProficiencyTestDateOptions = useCallback((options: Options<string>) => {
        dispatch(analysisProtocolCorrectionAction.setProficiencyTestDateOptions(options));
    }, [dispatch]);
    const setStableOptions = useCallback((options: Options<string>) => {
        dispatch(analysisProtocolCorrectionAction.setStableOptions(options));
    }, [dispatch]);
    const getAnalysisProtocol = useCallback((earTagValue: EarTag, proficiencyTestDateValue: Date) => {
        dispatch(analysisProtocolCorrectionAction.getAnalysisProtocol(earTagValue, proficiencyTestDateValue));
    }, [dispatch]);
    const getStableDateRelation = useCallback((stableCode: StableCode, proficiencyTestDateValue: Date) => {
        dispatch(analysisProtocolCorrectionAction.getStableDateRelation(stableCode, proficiencyTestDateValue));
    }, [dispatch]);
    const asyncValidate = useCallback((focusSubmit: () => void, getStableDateRelations?: boolean) => {
        dispatch(analysisProtocolCorrectionAction.validateCorrectionReport(focusSubmit, getStableDateRelations));
    }, [dispatch]);

    const correctionType = useSelector(simpleAnalysisProtocolCorrectionSelector.correctionType);
    const stableDateRelations = useSelector(simpleAnalysisProtocolCorrectionSelector.stableDateRelations);
    const proficiencyTestDateOptions = useSelector(simpleAnalysisProtocolCorrectionSelector.proficiencyTestDateOptions);
    const stableOptions = useSelector(simpleAnalysisProtocolCorrectionSelector.stableOptions);
    const stableDateOptionsLoading = useSelector(stableDateOptionsLoadingSelector);
    const analysisProtocolLoading = useSelector(analysisProtocolLoadingSelector);
    const formValuesForDependencies = useSelector(formHelpers.formValues(apcReportFormName))
        .orElse(initialApcReportFormValues);
    const {
        stableNumber,
        proficiencyTestDate,
        earTag,
        technicianNumber,
    } = formValuesForDependencies;
    const editingCorrectionReport = useSelector(
        simpleAnalysisProtocolCorrectionSelector.editingCorrectionReportId,
    ) !== undefined;

    const isUpdateCorrection = correctionType === 'UPDATE';
    const focusStableNumber = useCallback(() => {
        setTimeout(() => stableNumberRef.current?.focus());
    }, []);
    // eslint-disable-next-line max-lines-per-function
    useEffect(() => {
        // run after stableDateRelations fetch
        // eslint-disable-next-line max-lines-per-function
        stableDateRelations.onSome(relations => {
            if (relations.tag === 'ApcDateListWithStables') { // protocol exist
                if (editingCorrectionReport) {
                    pipe(relations.dateData, getDateOptions, setProficiencyTestDateOptions);
                }
                if (isUpdateCorrection) {
                    pipe(relations.stableData, getStableOptions, setStableOptions);
                    if (stableNumber) {
                        pipe(
                            stableNumber,
                            genProtocolDateOptionsBasedOnStable(relations),
                            setProficiencyTestDateOptions,
                        );
                    } else {
                        pipe(relations.dateData, getDateOptions, setProficiencyTestDateOptions);
                    }
                    setTimeout(() => proficiencyTestDateRef.current?.focus());
                } else if (proficiencyTestDate) {
                    pipe(proficiencyTestDate, genProtocolStableOptionsBasedOnDate(relations), setStableOptions);
                    setTimeout(() => proficiencyTestDateRef.current?.focus());
                }
            } else {
                if (stableNumber) {
                    pipe(
                        stableNumber,
                        genNonProtocolDateOptionsBasedOnStable(relations),
                        setProficiencyTestDateOptions,
                    );
                }
                focusStableNumber();
            }
            if (!editingCorrectionReport) proficiencyTestDateRef.current?.focus();
        });
    }, [setStableOptions, stableDateRelations]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (editingCorrectionReport) technicianNumberRef.current?.focus();
    }, [editingCorrectionReport, technicianNumberRef]);

    const {Form, Fields, submitting, asyncValidating, invalid, change} = useForm({
        form: apcReportFormName,
        initialValues: initialApcReportFormValues,
        validate,
        onSubmit,
    });

    const correctionTypeOptionsMemoized = useMemo(() => correctionTypeOptions(t), [t]);

    const asyncValidateDebounced = useMemo(() => debounce(debounceTime, asyncValidate), [asyncValidate]);

    const focusSubmit = useCallback(() => {
        setTimeout(() => submitRef.current?.focus());
    }, []);
    const focusProficiencyTestDate = useCallback(() => {
        setTimeout(() => proficiencyTestDateRef.current?.focus());
    }, []);

    const asyncValidateWithoutRelations = useCallback(() => {
        asyncValidateDebounced(focusSubmit);
    }, [asyncValidateDebounced, focusSubmit]);

    const onCorrectionTypeChange = useCallback((value: Opt<CorrectionType>) => {
        asyncValidateWithoutRelations();
        dispatch(formHelpers.initialize(
            apcReportFormName,
            pipe(
                initialApcReportFormValues,
                O.set(formValuesO.prop('correctionType'))(value.orNull()),
                O.set(formValuesO.prop('technicianNumber'))(technicianNumber),
            ),
        ));
        dispatch(analysisProtocolCorrectionAction.clearStableAndDateData());
        setTimeout(() => technicianNumberRef.current?.focus());
    }, [
        asyncValidateWithoutRelations,
        dispatch,
        technicianNumber,
        technicianNumberRef,
    ]);

    const onEarTagChange = useCallback(() => {
        setStableOptions([]);
        setProficiencyTestDateOptions([]);
        asyncValidateDebounced(focusProficiencyTestDate, true);
        change('milkingTripleAttribute', null);
        change('milkKg', null);
        change('fatPercentage', null);
        change('proteinPercentage', null);
        change('lactosePercentage', null);
        change('somaticCells', null);
        change('urea', null);
        change('proficiencyTestDate', null);
        change('stableNumber', null);
        dispatch(analysisProtocolCorrectionAction.clearStableAndDateData());
    }, [
        setStableOptions,
        setProficiencyTestDateOptions,
        asyncValidateDebounced,
        focusProficiencyTestDate,
        change,
        dispatch,
    ]);

    const analysisProtocol = useSelector(simpleAnalysisProtocolCorrectionSelector.analysisProtocol);
    const formValues = useSelector(formHelpers.formValues(apcReportFormName))
        .orElse(initialApcReportFormValues);
    const formState = useSelector(formHelpers.formState(apcReportFormName));

    const fieldsWithError = useSelector(formHelpers.getFieldsWithErrors(apcReportFormName));

    const {
        onStableNumberChange,
        onProficiencyTestDateChange,
    } = useDateAndStableChange({
        milkKgRef,
        isUpdateCorrection,
        proficiencyTestDateRef,
        stableNumberRef,
        earTagChangeRef,
        submitRef,
        stableDateRelations,
        earTag,
        stableNumber,
        getStableDateRelation,
        getAnalysisProtocol,
        setProficiencyTestDateOptions,
        correctionType,
        setStableOptions,
        change,
    });

    const proficiencyTestDateDisabled = useCallback((): boolean => {
        if (proficiencyTestDateOptions.forAll(isEmpty)) return true;
        if (editingCorrectionReport) return false;
        if (isUpdateCorrection) return false;

        return !protocolExists(stableDateRelations);
    }, [editingCorrectionReport, isUpdateCorrection, proficiencyTestDateOptions, stableDateRelations]);

    const stableNumberDisabled =
       protocolExists(stableDateRelations)
       && correctionType !== 'UPDATE'
       && isEmpty(proficiencyTestDate) || stableOptions.forAll(isEmpty);

    const onTechnicianNumberChange = useCallback((value: Opt<string>) => {
        if (value.testReOrFalse(technicianNumberRegexGen())) {
            if (editingCorrectionReport) {
                if (stableNumberDisabled) {
                    setTimeout(() => proficiencyTestDateRef.current?.focus());
                } else {
                    setTimeout(() => stableNumberRef.current?.focus());
                }
            } else {
                setTimeout(() => earTagRef.current?.focus());
            }
        }
    }, [earTagRef, editingCorrectionReport, stableNumberDisabled]);

    const submitDisabled = useMemo(() => submitting
        || invalid
        || Boolean(asyncValidating)
        || stableDateOptionsLoading
        || analysisProtocolLoading
    , [
        analysisProtocolLoading,
        asyncValidating,
        stableDateOptionsLoading,
        submitting,
        invalid,
    ]);

    useEffect(() => {
        if (!submitDisabled && correctionType === 'DISCARD' && technicianNumber) {
            setTimeout(() => submitRef.current?.focus());
        }
    }, [correctionType, submitDisabled, technicianNumber]);

    const onUpdatedEarTagChange = useCallback(() => {
        asyncValidateWithoutRelations();
    }, [asyncValidateWithoutRelations]);

    const focusNextInputIfValid = useCallback((
        currentField: keyof ApcReportFormValues,
        nextFieldRef: RefObject<HTMLInputElement>,
        inputType: NumberInputType,
        value: string,
        isSubmitRef?: boolean,
    ): void => {
        if (testRe(inputType === 'decimal52' ? decimal52Regex : decimal80Regex)(value)) {
            const numberValue = Number.parseFloat(replace(',', '.')(value));
            change(currentField, roundNumberToPrecision(numberValue, inputType));
            setTimeout(() => {
                nextFieldRef.current?.focus();
                if (!isSubmitRef) nextFieldRef.current?.select();
            });
        } else {
            dispatch(formHelpers.touch(apcReportFormName, currentField));
        }
    }, [change, dispatch]);

    const onMilkKgInput = useCallback((value: string) => {
        const isFatPercentageFieldDisabled = fatPercentageRef.current?.disabled;
        if (testRe(decimal52Regex)(value)) {
            const numberValue = Number.parseFloat(replace(',', '.')(value));
            change('milkKg', roundNumberToPrecision(numberValue, 'decimal52'));
            setTimeout(() => {
                if (!isFatPercentageFieldDisabled) {
                    fatPercentageRef.current?.select();
                } else {
                    submitRef.current?.focus();
                }
            });
        } else {
            dispatch(formHelpers.touch(apcReportFormName, 'milkKg'));
        }
    }, [change, dispatch]);
    const onFatPercentageInput = useCallback((value: string) => {
        focusNextInputIfValid('fatPercentage', proteinPercentageRef, 'decimal52', value);
    }, [focusNextInputIfValid]);
    const onProteinPercentageInput = useCallback((value: string) => {
        focusNextInputIfValid('proteinPercentage', lactosePercentageRef, 'decimal52', value);
    }, [focusNextInputIfValid]);
    const onLactosePercentageInput = useCallback((value: string) => {
        focusNextInputIfValid('lactosePercentage', somaticCellsRef, 'decimal52', value);
    }, [focusNextInputIfValid]);
    const somaticCellMaxLength = 8;
    const onSomaticCellsInput = useCallback((value: string) => {
        if (testRe(positiveOrZeroDecimal80RegexGen)(value) && value.length === somaticCellMaxLength) {
            const numberValue = Number.parseFloat(replace(',', '.')(value));
            change('somaticCells', roundNumberToPrecision(numberValue, 'positiveOrZeroDecimal80'));
            setTimeout(() => {
                ureaRef.current?.focus();
                ureaRef.current?.select();
            });
        } else {
            dispatch(formHelpers.touch(apcReportFormName, 'somaticCells'));
        }
    }, [change, dispatch]);

    const onUreaInput = useCallback((value: string) => {
        if (testRe(decimal52Regex)(value)) {
            const numberValue = Number.parseFloat(replace(',', '.')(value));
            change('urea', roundNumberToPrecision(numberValue, 'decimal52'));
            setTimeout(() => {
                submitRef.current?.focus();
            });
        } else {
            dispatch(formHelpers.touch(apcReportFormName, 'urea'));
        }
    }, [change, dispatch]);

    const isAnalysisProtocolFieldZeroOrNull = useCallback((field: keyof ApcReportFormValues): boolean => {
        const extractedField = formValues[field];

        return extractedField === 0 || extractedField === null;
    }, [formValues]);

    const hasValueAndNotTouched = useCallback((field: keyof ApcReportFormValues): boolean => {
        const active = formState.prop('fields').prop(field).prop('visited').orFalse();

        return !active && !isAnalysisProtocolFieldZeroOrNull(field);
    }, [formState, isAnalysisProtocolFieldZeroOrNull]);

    const isAdditionalFieldDisabled = useCallback((field: keyof ApcReportFormValues): boolean => {
        if (!isUpdateCorrection) return true;

        return hasValueAndNotTouched(field) && !fieldsWithError.exists(includes(field));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysisProtocol, onProficiencyTestDateChange, isUpdateCorrection, fieldsWithError]);

    return {
        Form,
        Fields,
        correctionTypeOptions: correctionTypeOptionsMemoized,
        correctionType,
        editingCorrectionReport,
        proficiencyTestDateOptions,
        stableOptions,
        stableDateOptionsLoading,
        technicianNumberRef,
        earTagChangeRef,
        milkKgRef,
        proficiencyTestDateRef,
        stableNumberRef,
        tCommon,
        t,
        proficiencyTestDateDisabled,
        stableNumberDisabled,
        submitDisabled,
        fatPercentageRef,
        proteinPercentageRef,
        lactosePercentageRef,
        somaticCellsRef,
        ureaRef,
        submitRef,
        processingForm: asyncValidating === true || submitting,
        onTechnicianNumberChange,
        onEarTagChange,
        onUpdatedEarTagChange,
        onProficiencyTestDateChange,
        onStableNumberChange,
        isUpdateCorrection,
        onCorrectionTypeChange,
        onMilkKgInput,
        onFatPercentageInput,
        onProteinPercentageInput,
        onLactosePercentageInput,
        onSomaticCellsInput,
        onUreaInput,
        isAdditionalFieldDisabled,
    };
};
